import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type GoogleReview = {
  author_name: string;
  author_url?: string | null;
  profile_photo_url?: string | null;
  rating: number;
  text: string;
  relative_time_description: string;
  time: number;
};

export type GoogleReviewsPayload = {
  reviews: GoogleReview[];
  rating: number | null;
  rating_count: number | null;
  updated_at: string | null;
  place_id: string | null;
};

const STALE_MS = 6 * 60 * 60 * 1000; // 6 hours

async function assertAdmin(supabase: any, userId: string) {
  const { data } = await supabase
    .from("admin_users")
    .select("user_id")
    .eq("user_id", userId)
    .maybeSingle();
  if (!data) throw new Error("Forbidden");
}

async function fetchFromGoogle(placeId: string): Promise<{
  reviews: GoogleReview[];
  rating: number | null;
  rating_count: number | null;
}> {
  const apiKey = process.env.LOVABLE_API_KEY;
  const connKey = process.env.GOOGLE_MAPS_API_KEY;
  if (!apiKey || !connKey) throw new Error("Google Maps connector not configured");

  const res = await fetch(
    `https://connector-gateway.lovable.dev/google_maps/places/v1/places/${encodeURIComponent(placeId)}?languageCode=en`,
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "X-Connection-Api-Key": connKey,
        "X-Goog-FieldMask": "reviews,rating,userRatingCount",
      },
    },
  );
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Google Places error ${res.status}: ${body}`);
  }
  const data = (await res.json()) as any;
  const reviews: GoogleReview[] = (data.reviews ?? []).map((r: any) => ({
    author_name: r.authorAttribution?.displayName ?? "Google user",
    author_url: r.authorAttribution?.uri ?? null,
    profile_photo_url: r.authorAttribution?.photoUri ?? null,
    rating: Number(r.rating ?? 5),
    text: r.text?.text ?? r.originalText?.text ?? "",
    relative_time_description: r.relativePublishTimeDescription ?? "",
    time: r.publishTime ? Math.floor(new Date(r.publishTime).getTime() / 1000) : 0,
  }));
  return {
    reviews,
    rating: typeof data.rating === "number" ? data.rating : null,
    rating_count: typeof data.userRatingCount === "number" ? data.userRatingCount : null,
  };
}

async function loadAndMaybeRefresh(force = false): Promise<GoogleReviewsPayload> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: row } = await supabaseAdmin
    .from("app_settings")
    .select("google_place_id, google_reviews, google_rating, google_rating_count, google_reviews_updated_at")
    .eq("id", 1)
    .maybeSingle();

  const placeId = (row?.google_place_id as string | null) ?? null;
  const cached: GoogleReviewsPayload = {
    reviews: (row?.google_reviews as GoogleReview[]) ?? [],
    rating: (row?.google_rating as number | null) ?? null,
    rating_count: (row?.google_rating_count as number | null) ?? null,
    updated_at: (row?.google_reviews_updated_at as string | null) ?? null,
    place_id: placeId,
  };

  if (!placeId) return cached;

  const age = cached.updated_at ? Date.now() - new Date(cached.updated_at).getTime() : Infinity;
  if (!force && age < STALE_MS && cached.reviews.length > 0) return cached;

  try {
    const fresh = await fetchFromGoogle(placeId);
    const updated_at = new Date().toISOString();
    await supabaseAdmin
      .from("app_settings")
      .update({
        google_reviews: fresh.reviews,
        google_rating: fresh.rating,
        google_rating_count: fresh.rating_count,
        google_reviews_updated_at: updated_at,
      })
      .eq("id", 1);
    return { ...fresh, updated_at, place_id: placeId };
  } catch (e) {
    // Network/quota issue — return what we have so the site keeps rendering.
    if (cached.reviews.length > 0) return cached;
    throw e;
  }
}

export const getGoogleReviews = createServerFn({ method: "GET" }).handler(async () => {
  return loadAndMaybeRefresh(false);
});

export const refreshGoogleReviews = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    return loadAndMaybeRefresh(true);
  });

export const saveGooglePlaceId = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { placeId: string }) => d)
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const placeId = data.placeId.trim() || null;
    await supabaseAdmin
      .from("app_settings")
      .update({
        google_place_id: placeId,
        google_reviews: [],
        google_rating: null,
        google_rating_count: null,
        google_reviews_updated_at: null,
      })
      .eq("id", 1);
    return { ok: true };
  });
